import { makeStyles } from '@material-ui/core';
import { fade } from '@material-ui/core/styles/colorManipulator';

const EXCEL_GREEN = '#2e7d32';

export const useStyles = makeStyles(theme => {
  const dark = theme.palette.type === 'dark';
  const successColor = dark ? '#81c784' : '#1b5e20';
  const errorColor = dark ? '#e57373' : '#8e1b1b';

  return {
    /* Общие */
    grow: {
      flexGrow: 1,
      minWidth: 0
    },
    medium: {
      fontWeight: 500
    },
    required: {
      color: theme.palette.error.main
    },
    link: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      minHeight: 44,
      fontWeight: 500,
      flexShrink: 0
    },

    /* Кнопка и модальное окно */
    importButton: {
      marginLeft: 10
    },
    paper: {
      maxWidth: 640
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: 16,
      padding: '20px 28px'
    },
    actions: {
      padding: '16px 28px 24px'
    },

    /* Шаблон */
    template: {
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      padding: '10px 16px',
      borderRadius: 4,
      backgroundColor: dark ? theme.palette.action.hover : '#f1f5f9'
    },
    excelIcon: {
      fontSize: 28,
      color: EXCEL_GREEN
    },

    /* Выбор файла */
    dropzone: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      padding: '20px 24px',
      textAlign: 'center',
      borderRadius: 6,
      border: `2px dashed ${fade(theme.palette.primary.main, 0.55)}`,
      backgroundColor: fade(theme.palette.primary.main, 0.03),
      transition: 'border-color 150ms, background-color 150ms'
    },
    dropzoneActive: {
      borderColor: theme.palette.primary.main,
      backgroundColor: fade(theme.palette.primary.main, 0.1)
    },
    dropzoneDisabled: {
      borderColor: theme.palette.border.default,
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.secondary
    },
    uploadIcon: {
      fontSize: 32,
      color: theme.palette.primary.main
    },
    fileCard: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '6px 4px 6px 14px',
      borderRadius: 4,
      border: `1px solid ${fade(theme.palette.primary.main, 0.35)}`
    },
    successIcon: {
      color: EXCEL_GREEN
    },
    errorCard: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '6px 4px 6px 14px',
      borderRadius: 4,
      border: `1px solid ${theme.palette.error.main}`,
      backgroundColor: dark ? fade(theme.palette.error.main, 0.12) : '#fdecea',
      color: dark ? theme.palette.error.light : errorColor
    },

    /* Загрузка */
    progress: {
      display: 'flex',
      flexDirection: 'column',
      gap: 36,
      padding: '40px 0'
    },
    progressHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginBottom: 10
    },
    progressBar: {
      height: 6,
      borderRadius: 3,
      marginBottom: 10
    },

    /* Выбор организации */
    orgLegend: {
      fontSize: 13,
      fontWeight: 500,
      marginBottom: 8,
      color: theme.palette.primary.main
    },
    orgLabel: {
      fontSize: 13,
      fontWeight: 500,
      marginBottom: 6,
      color: theme.palette.text.primary
    },
    orgBox: {
      border: `1px solid ${theme.palette.border.default}`,
      borderRadius: 4,
      overflow: 'hidden'
    },
    orgFilters: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      gap: 8,
      padding: 10,
      backgroundColor: theme.palette.action.hover,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    orgFilterLabel: {
      fontSize: 12,
      marginBottom: 4,
      color: theme.palette.text.secondary
    },
    orgLoaderSpacer: {
      height: 4
    },
    orgTableWrap: {
      height: 232,
      overflow: 'auto',
      position: 'relative'
    },
    orgTable: {
      width: '100%',
      borderCollapse: 'collapse',
      tableLayout: 'fixed',
      fontSize: 13,
      '& th': {
        textAlign: 'left',
        fontWeight: 500,
        padding: '8px 10px',
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`
      },
      '& td': {
        padding: '9px 10px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        borderBottom: `1px solid ${theme.palette.divider}`
      }
    },
    orgColId: {
      width: 70
    },
    orgColCode: {
      width: 115
    },
    orgRow: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    },
    orgRowSelected: {
      fontWeight: 500,
      backgroundColor: `${fade(theme.palette.primary.main, 0.14)} !important`
    },
    orgPlaceholder: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      padding: 16,
      textAlign: 'center'
    },
    orgFooter: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '4px 4px 4px 10px',
      borderTop: `1px solid ${theme.palette.divider}`,
      fontSize: 12,
      color: theme.palette.text.secondary
    },
    orgField: {
      width: '100%',
      minHeight: 52,
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      padding: '6px 4px 6px 14px',
      border: `1px solid ${theme.palette.text.disabled}`,
      borderRadius: 4,
      cursor: 'pointer',
      '&:hover': {
        borderColor: theme.palette.text.primary
      }
    },

    /* Результат импорта */
    stats: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
      gap: 12
    },
    stat: {
      borderRadius: 4,
      padding: '12px 14px',
      backgroundColor: theme.palette.action.hover
    },
    statValue: {
      fontSize: 26,
      fontWeight: 500,
      lineHeight: 1.3
    },
    statSuccess: {
      color: successColor,
      backgroundColor: dark ? 'rgba(129, 199, 132, 0.12)' : '#e8f3ea'
    },
    statFailed: {
      color: errorColor,
      backgroundColor: dark ? 'rgba(229, 115, 115, 0.12)' : '#fdecea'
    },
    errorsHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      marginBottom: 8
    },
    resultTableWrap: {
      maxHeight: 260,
      overflow: 'auto',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 4
    },
    resultTable: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 13,
      '& th': {
        textAlign: 'left',
        fontWeight: 500,
        padding: '8px 12px',
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`
      },
      '& td': {
        padding: '8px 12px',
        borderBottom: `1px solid ${theme.palette.divider}`
      }
    },
    resultColRow: {
      width: 70
    },
    resultColField: {
      width: 160
    },
    errorText: {
      color: errorColor
    }
  };
});
